import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import React, { useEffect, useState } from "react";
import { createUser } from "../../api";
import { jwtDecode } from "jwt-decode";
import { getUsers, getPractice, sendPasswordResetEmail } from "../../api";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";

function MyUserModal({onUserUpdate = () => {}, ...props}) {
  const [pracName, setPracName] = useState([]);
  const [closeAlert, setCloseAlert] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [validated, setValidated] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [user, setUser] = useState({
    email: "",
    firstName: "",
    lastName: "",
    hpcsa: "",
    occupation: "",
    phoneNumber: "",
    role: [],
    practice: [],
    password: "",
  });

  useEffect(() => {
    async function getPracticeId() {
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        const allUsers = await getUsers();
        const findUser = allUsers.find((user) => user._id === decoded._id);
        const allPracs = await getPractice();
        const findPracName = allPracs.filter((prac) =>
          findUser.practice.includes(prac.practiceNumber)
        );
        setPracName(findPracName);
      }
    }
    getPracticeId();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const isValidEmailFormat = /\S+@\S+\.\S+/.test(user.email);


    if (!user.email || !isValidEmailFormat) newErrors.email = "Enter a valid email.";
    if (!user.firstName) newErrors.firstName = "First name is required.";
    if (!user.lastName) newErrors.lastName = "Last name is required.";
    if (!user.occupation) newErrors.occupation = "Occupation is required.";
    if (!user.phoneNumber || !/^\d{3}-\d{3}-\d{4}$/.test(user.phoneNumber))
      newErrors.phoneNumber = "Enter a valid phone number (xxx-xxx-xxxx).";
    if (!selectedRoles.length) newErrors.role = "At least one role must be selected.";
    if (!selectedPractices.length) newErrors.practice = "At least one practice must be selected.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await createUser({ ...user, role: selectedRoles, practice: selectedPractices });
      if (response.status === 200) {
        sendPasswordResetEmail(user.email).catch((error) => {
          console.error("Error during password reset request:", error);
        });
        showToast(
          "success",
          "User added successfully. They will receive an email to select their password.",
        );
        props.onHide();
        onUserUpdate();
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        setErrors((prev) => ({
          ...prev,
          email: error.response.data?.error || "Email already in use.",
        }));
        return;
      }
      console.error("Error creating user:", error);
      showToast("error", "Failed to add the user. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      const digits = value.replace(/\D/g, "");
      const formatted = digits.slice(0, 10).replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3");
      setUser((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setUser((prev) => ({ ...prev, [name]: value }));
    }

    // Clear the field-level error as soon as the user starts typing so the
    // duplicate-email message doesn't linger after they change the address.
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleRoleChange = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handlePracticeChange = (practiceNumber) => {
    setSelectedPractices((prev) =>
      prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber]
    );
  };

  function handleClose ()  {
    // An empty close should just close; only confirm when data was entered.
    const hasEntries =
      Object.values(user).some((v) => (Array.isArray(v) ? v.length : v)) ||
      selectedRoles.length ||
      selectedPractices.length;
    if (!hasEntries) {
      props.onHide();
      return;
    }
    setCloseAlert(true);
  };

const handleCancelNo = () => {
    setCloseAlert(false); // Hide alert and keep modal open
  };

const handleCancelYes = async () => {
    setCloseAlert(false);
    setUser({
      email: "",
      firstName: "",
      lastName: "",
      hpcsa: "",
      occupation: "",
      phoneNumber: "",
      role: [],
      practice: [],
      password: "",
    });
    setSelectedPractices([]);
    setSelectedRoles([]);
    props.onHide();
    // Close the modal after successful operation
};

  return (
    <>
    <Modal {...props} onHide={handleClose} size="lg" scrollable centered>
      <Modal.Header
        closeButton={!closeAlert}
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Add Team Member
        </Modal.Title>
      </Modal.Header>
      {closeAlert && (
        <Alert
          variant="info"
          className="d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
        >
          <span>Are you sure you want to close? The entered data will be removed.</span>
          <div className="ms-auto d-flex gap-2">
            <OverlayTrigger placement="top" overlay={<Tooltip>Discard and close</Tooltip>}>
              <Button variant="outline-primary" onClick={handleCancelYes}>
                <i className="fa-solid fa-check" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Keep editing</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleCancelNo}>
                <i className="fa-solid fa-xmark" />
              </Button>
            </OverlayTrigger>
          </div>
        </Alert>
      )}
      <Modal.Body>
        <Form id="add-user-form" noValidate validated={validated} onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                required
                name="email"
                isInvalid={!!errors.email}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                required
                placeholder="First Name"
                name="firstName"
                isInvalid={!!errors.firstName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                required
                placeholder="Last Name"
                name="lastName"
                isInvalid={!!errors.lastName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
            </Form.Group>
       

          <Form.Group as={Col} controlId="hpcsa">
              <Form.Label>HPCSA #</Form.Label>
              <Form.Control
                required
                placeholder="HPCSA number"
                name="hpcsa"
                onChange={handleChange}
              />
             
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridOccupation">
              <Form.Label>Occupation</Form.Label>
              <Form.Select
                required
                name="occupation"
                isInvalid={!!errors.occupation}
                onChange={handleChange}
              >
                <option>Choose...</option>
                <option>Administration</option>
                <option>Doctor</option>
                <option>Finance</option>
                <option>Nurse</option>
                <option>Occupational Therapist</option>
                <option>Podiatrist</option>
                <option>Physio Therapist</option>
                <option>Psychologist</option>
                <option>Speech Therapist</option>
                <option>Reception</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.occupation}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="phoneNumber">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                placeholder="xxx-xxx-xxxx"
                name="phoneNumber"
                value={user.phoneNumber}
                isInvalid={!!errors.phoneNumber}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="roleCheckboxes">
              <Form.Label>Roles</Form.Label>
              <div>
                {["Administration", "Practitioner", "Finance", "SuperAdmin"].map((role) => (
                  <Form.Check
                    key={role}
                    type="checkbox"
                    label={role}
                    checked={selectedRoles.includes(role)}
                    onChange={() => handleRoleChange(role)}
                  />
                ))}
              </div>
              {errors.role && <div className="text-danger small">{errors.role}</div>}
            </Form.Group>

            <Form.Group as={Col} controlId="practiceCheckboxes">
              <Form.Label>Practices</Form.Label>
              <div>
                {pracName.map((prac) => (
                  <Form.Check
                    key={prac.practiceNumber}
                    type="checkbox"
                    label={prac.practiceName}
                    checked={selectedPractices.includes(prac.practiceNumber)}
                    onChange={() => handlePracticeChange(prac.practiceNumber)}
                  />
                ))}
              </div>
              {errors.practice && <div className="text-danger small">{errors.practice}</div>}
            </Form.Group>
          </Row>

        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          type="submit"
          form="add-user-form"
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          {submitting ? <Spinner size="sm" animation="border" /> : "Add User"}
        </Button>
        <Button variant="outline-secondary" onClick={handleClose} style={{ minWidth: 140 }}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
    <AppToast
      show={toast.show}
      onClose={hideToast}
      variant={toast.variant}
      message={toast.message}
    />
    </>
  );
}

export function AddUser({ onUserUpdate }) {
  const [userModalShow, setUserModalShow] = useState(false);

  return (
    <div>
      <Button variant="primary" className="me-2 w-100" onClick={() => setUserModalShow(true)}>
        Add User
      </Button>
      <MyUserModal show={userModalShow} onHide={() => setUserModalShow(false)} onUserUpdate={onUserUpdate} />
    </div>
  );
}
